"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ISDESubsidy2021 = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const solution_1 = require("../solution");
const sum_1 = require("../utils/sum");
const calculator_1 = require("./calculator");
const types_1 = require("./types");
const utils_1 = require("./utils");
class ISDESubsidy2021 extends calculator_1.ISDESubsidyCalculator {
    constructor() {
        super(...arguments);
        this.year = 2021;
        this.bySolution = {
            [solution_1.Solution.AtticFloorInsulation]: new types_1.SubsidyDetails(8, { min: 20, max: 200 }),
            [solution_1.Solution.CentralHeatingBoiler]: null,
            [solution_1.Solution.ChargingBox]: null,
            [solution_1.Solution.CrawlspaceInsulation]: new types_1.SubsidyDetails(6, { min: 20, max: 130 }),
            [solution_1.Solution.ElectricHeatPump]: new types_1.SubsidyDetails(2500, { min: 1, max: 3 }),
            [solution_1.Solution.AirToAirHeatPump]: null,
            [solution_1.Solution.FloorInsulation]: new types_1.SubsidyDetails(11, { min: 20, max: 130 }),
            [solution_1.Solution.HybridHeatPump]: new types_1.SubsidyDetails(1700, { min: 1, max: 3 }),
            [solution_1.Solution.InnerRoofInsulation]: new types_1.SubsidyDetails(30, { min: 20, max: 200 }),
            [solution_1.Solution.Miscellaneous]: null,
            [solution_1.Solution.PvSystem]: null,
            [solution_1.Solution.Sedum]: null,
            [solution_1.Solution.WallInsulation]: new types_1.SubsidyDetails(8, { min: 10, max: 170 }),
            [solution_1.Solution.WindowFrames]: new types_1.SubsidyDetails(23, { min: 8, max: 45 }),
            [solution_1.Solution.WindowGlazing]: new types_1.SubsidyDetails(53, { min: 8, max: 45 }),
            [solution_1.Solution.UnderfloorHeating]: null,
            [solution_1.Solution.HomeBattery]: null,
        };
        this.eligibleWithin = {
            start: new Date('2020-01-01'),
            end: (0, dayjs_1.default)('2021-12-31').endOf('day').toDate(),
        };
    }
    /**
     * @returns true when total number of subsidizable solutions is >= 2 or includes a heat pump
     */
    isSubsidizableSolutions(...solutions) {
        const subsidizableSolutions = [...new Set(solutions)].filter(solution => this.getDetailsBySolution(solution) !== null);
        return (subsidizableSolutions.length >= 2 ||
            subsidizableSolutions.includes(solution_1.Solution.ElectricHeatPump) ||
            subsidizableSolutions.includes(solution_1.Solution.HybridHeatPump));
    }
    forInstallables(installables) {
        // Note: subsidy on a set of installables can only be requested in the current year when the installables were all installed at most 1 year ago.
        // However, since 2021 is a previous year, we don't explicitly check that requirement anymore
        // so that we can use this class to support subsidy calculations for older quotes.
        // combine installables with the same solution
        installables = (0, utils_1.mergeSameSolutionInstallables)(installables);
        const solutions = installables.map(i => i.solution);
        // in 2021 it was still allowed to combine floor insulation with crawlspace insulation for subsidy
        if (solutions.includes(solution_1.Solution.FloorInsulation) &&
            solutions.includes(solution_1.Solution.CrawlspaceInsulation)) {
            // find the floor- and crawlspace insulation quotes
            const [floorInstallable, replaceFloor] = (0, utils_1.findBySolution)(installables, solution_1.Solution.FloorInsulation); // eslint-disable-line prettier/prettier
            const [crawlspaceInstallable, replaceCrawlspace] = (0, utils_1.findBySolution)(installables, solution_1.Solution.CrawlspaceInsulation); // eslint-disable-line prettier/prettier
            // total amount of subsidisable product on floor installable
            const floorProductsAmount = (0, utils_1.sumSubsidisableProductAmounts)(floorInstallable.items);
            // total amount of subsidisable product on crawlspace installable
            const crawlspaceProductsAmount = (0, utils_1.sumSubsidisableProductAmounts)(crawlspaceInstallable.items);
            // augment the floor- and crawlspace installables with a zero-subsidy item with the amount of the each other,
            // such that the floor- and crawlspace product amounts together count towards the minimum and maximum amounts for which subsidy can be requested,
            // but they will each still be counted with their respective monetary amounts per unit.
            const newFloor = {
                solution: solution_1.Solution.FloorInsulation,
                items: [...floorInstallable.items, new types_1.ZeroSubsidyItem(crawlspaceProductsAmount)],
                installationDate: floorInstallable.installationDate,
            };
            const newCrawlspace = {
                solution: solution_1.Solution.CrawlspaceInsulation,
                items: [...crawlspaceInstallable.items, new types_1.ZeroSubsidyItem(floorProductsAmount)],
                installationDate: crawlspaceInstallable.installationDate,
            };
            replaceFloor(newFloor);
            replaceCrawlspace(newCrawlspace);
            (0, utils_1.remove)(solutions, solution_1.Solution.CrawlspaceInsulation);
        }
        // Remove hybrid heat pump subsidy if electric heat pump is also selected
        if (solutions.includes(solution_1.Solution.HybridHeatPump) &&
            solutions.includes(solution_1.Solution.ElectricHeatPump)) {
            (0, utils_1.remove)(installables, i => i.solution === solution_1.Solution.HybridHeatPump);
            (0, utils_1.remove)(solutions, solution_1.Solution.HybridHeatPump);
        }
        if (!this.isSubsidizableSolutions(...solutions))
            return 0;
        return (0, sum_1.sum)(installables.map(this.forInstallable.bind(this)));
    }
}
exports.ISDESubsidy2021 = ISDESubsidy2021;
